// Friendly label for a target_url stored on optimization_attempts (or
// emitted by Owen's intent.executable.targetUrl). Operator wants to
// know "what page got changed" at a glance — not parse a full URL.
//
// "/" or "" → "Homepage"
// "/services" → "Services" (capitalized)
// "/services/emergency-tree.html" → "Service: emergency tree"
// "/blog/why-pipes-burst-2026.html" → "Blog: why pipes burst 2026"
// "/about.html" → "About"
// fallback → the path verbatim
//
// Pure — no I/O.

export function pageLabel(targetUrl: string | null | undefined): string {
  if (!targetUrl) return "—";
  let path: string;
  try {
    const u = new URL(targetUrl);
    path = u.pathname;
  } catch {
    // not a valid URL — assume the caller passed a bare path.
    path = targetUrl;
  }
  // Normalize: strip trailing slash + trailing .html so the label is
  // readable. Keep the leading slash so all branches start the same.
  const norm = path.replace(/\/$/, "").replace(/\.html$/i, "");
  if (norm === "" || norm === "/") return "Homepage";

  const parts = norm.split("/").filter(Boolean);

  // /blog/<slug> → Blog: <slug-humanized>
  if (parts[0] === "blog" && parts.length >= 2) {
    return `Blog: ${humanize(parts[1] ?? "")}`;
  }
  // /services/<slug> → Service: <slug-humanized>
  if (parts[0] === "services" && parts.length >= 2) {
    return `Service: ${humanize(parts[1] ?? "")}`;
  }
  // /services or single segment like /about, /contact, /how-it-works
  if (parts.length === 1) {
    return humanize(parts[0] ?? "", { titleCase: true });
  }
  // Anything else: keep the path so the operator can see what it is.
  return `/${parts.join("/")}`;
}

function humanize(slug: string, opts: { titleCase?: boolean } = {}): string {
  const words = slug.replace(/[-_]+/g, " ").trim();
  if (!opts.titleCase) return words.toLowerCase();
  return words
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}
